import type * as replay from '@amplitude/session-replay-browser';

import * as cookie from 'js/lib/cookie';
import { isPoweruser } from 'js/lib/user';

import type { Plugin } from '@coursera/event-pulse/core';

import FrontendInfraExperiments from 'bundles/epic/clients/FrontendInfra';
import { shouldLoadAdminGTM } from 'bundles/event-tracking/shouldLoadAdTracking';

import session from './session';

const API_KEY = '9f370ddd4287ab67b08ab58273ddf40c';
const DEFAULT_SAMPLE_RATE = 0.3;
const ADMIN_SAMPLE_RATE = 1;

declare global {
  interface Window {
    sessionReplay?: typeof replay;
  }
}

/**
 * Initialize the session replay plugin
 * NOTE: This is only initialized for non-superuser users.
 * @ignore exported for testing purposes
 * @returns The session replay instance
 */
export function sessionReplayInit() {
  // Session Replay is loaded by GTM, so we need to check if it's available
  if (window?.sessionReplay && !window.sessionReplay.getSessionId() && !isPoweruser()) {
    window.sessionReplay.init(API_KEY, {
      // Sadly, we are not using the device ID from our client
      deviceId: cookie.get('__204u') ?? '',
      // Session for Amplitude is only the timestamp when the session started
      // Our uuid is a combination of the timestamp and a random uuid
      // @see static/bundles/page/lib/event-pulse/core/session.ts
      sessionId: Number(session.get().uuid.split('~')[1]),
      // 10% for admin side and 1% for learner side
      sampleRate: shouldLoadAdminGTM() ? ADMIN_SAMPLE_RATE : DEFAULT_SAMPLE_RATE,
    });
  }

  return window.sessionReplay;
}

const plugin: Plugin = (client) => {
  let sessionReplay: ReturnType<typeof sessionReplayInit> | undefined;

  client.middleware(async (eventName, data) => {
    if (!FrontendInfraExperiments.get('replayPOC')) {
      return data;
    }

    if (!sessionReplay) {
      sessionReplay = sessionReplayInit();
    }

    // By convention all schemas that have a `page` property should allow session replay id to be add
    if (sessionReplay && Object.hasOwn(data, 'page')) {
      return { ...data, ...sessionReplay.getSessionReplayProperties() };
    }

    return data;
  });
};

export default plugin;
